import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Gauge, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default efficiency thresholds (fallback if API doesn't return them)
const DEFAULT_THRESHOLDS = {
  cpl: { target: 404.57, max: 484.94 },
  cps: { target: 909.81, max: 1189.64 },
  cpsale: { target: 3486.24, max: 4621.82 },
  roas: { target: 6.66, min: 5 },
  com: { target: 15, max: 20 },
  nsli: { target: 8000, min: 6000 }
};

// Threshold metadata (static - labels, descriptions, etc.)
const THRESHOLD_META = {
  cpl: { label: 'CPL', description: 'Cost Per Lead', lowerIsBetter: true },
  cps: { label: 'CPS', description: 'Cost Per Set', lowerIsBetter: true },
  cpsale: { label: 'CPSale', description: 'Cost Per Sale', lowerIsBetter: true },
  roas: { label: 'ROAS', description: 'Return on Ad Spend', lowerIsBetter: false },
  com: { label: 'COM %', description: 'Cost of Marketing', lowerIsBetter: true },
};

// Format helpers
const formatValue = (value, key) => {
  if (value === null || value === undefined) return '-';
  if (key === 'roas') return `${Number(value).toFixed(2)}x`;
  if (key === 'com') return `${Number(value).toFixed(1)}%`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${Number(value).toFixed(0)}`;
};

// Get status based on threshold
const getEfficiencyStatus = (actual, threshold) => {
  if (actual === null || actual === undefined) return 'neutral';
  
  const { target, min, max, lowerIsBetter } = threshold;
  
  if (lowerIsBetter) {
    // For CPL, CPS, CPSale, COM - lower is better
    if (actual <= target) return 'excellent';
    if (actual <= max) return 'acceptable';
    return 'poor';
  } else {
    // For ROAS - higher is better
    if (actual >= target) return 'excellent';
    if (actual >= min) return 'acceptable';
    return 'poor';
  }
};

const statusConfig = {
  excellent: {
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/20',
    border: 'border-green-500',
    label: 'Excellent',
    icon: CheckCircle2,
  },
  acceptable: {
    color: 'text-yellow-600 dark:text-yellow-400',
    bg: 'bg-yellow-100 dark:bg-yellow-900/20',
    border: 'border-yellow-500',
    label: 'Needs Attention',
    icon: AlertTriangle,
  },
  poor: {
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/20',
    border: 'border-red-500',
    label: 'Above Max',
    icon: TrendingDown,
  },
  neutral: {
    color: 'text-slate-500',
    bg: 'bg-slate-100 dark:bg-slate-800',
    border: 'border-slate-300',
    label: 'No Data',
    icon: Gauge,
  },
};

// Efficiency Gauge component
const EfficiencyGauge = ({ metricKey, actual, threshold }) => {
  const status = getEfficiencyStatus(actual, threshold);
  const config = statusConfig[status];
  const StatusIcon = config.icon;
  
  const { target, min, max, lowerIsBetter, label, description } = threshold;
  
  // Calculate gauge percentage
  const calculateGaugePercent = () => {
    if (actual === null || actual === undefined) return 50;
    
    if (lowerIsBetter) {
      // For lower-is-better metrics, 100% at 0, 0% at max
      const percent = 100 - (actual / max) * 100;
      return Math.max(0, Math.min(100, percent));
    } else {
      // For higher-is-better metrics, 0% at min, 100% at target
      const range = target - (min || 0);
      const percent = ((actual - (min || 0)) / range) * 100;
      return Math.max(0, Math.min(100, percent));
    }
  };
  
  const gaugePercent = calculateGaugePercent();
  
  return (
    <div className={`p-4 rounded-lg border-l-4 ${config.border} ${config.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-slate-900 dark:text-white">{label}</h4>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <StatusIcon className={`h-5 w-5 ${config.color}`} />
      </div>
      
      {/* Current Value */}
      <div className={`text-2xl font-bold ${config.color} mb-3`}>
        {formatValue(actual, metricKey)}
      </div>
      
      {/* Gauge bar */}
      <div className="relative h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-2">
        {/* Target marker */}
        <div 
          className="absolute top-0 w-0.5 h-full bg-slate-400 dark:bg-slate-500 z-10"
          style={{ left: lowerIsBetter ? `${100 - (target / max) * 100}%` : `${((target - (min || 0)) / (target - (min || 0))) * 100}%` }}
        />
        {/* Value bar */}
        <div 
          className={`h-full rounded-full transition-all ${
            status === 'excellent' ? 'bg-green-500' :
            status === 'acceptable' ? 'bg-yellow-500' :
            status === 'poor' ? 'bg-red-500' : 'bg-slate-400'
          }`}
          style={{ width: `${gaugePercent}%` }}
        />
      </div>
      
      {/* Thresholds */}
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Target: {formatValue(target, metricKey)}</span>
        <span className="text-orange-500">{lowerIsBetter ? 'Max' : 'Min'}: {formatValue(lowerIsBetter ? max : min, metricKey)}</span>
      </div>
      
      {/* Status badge */}
      <div className="mt-3">
        <Badge className={`text-xs ${
          status === 'excellent' ? 'bg-green-500 hover:bg-green-600' :
          status === 'acceptable' ? 'bg-yellow-500 hover:bg-yellow-600' :
          status === 'poor' ? 'bg-red-500 hover:bg-red-600' : 'bg-slate-400'
        }`}>
          {config.label}
        </Badge>
      </div>
    </div>
  );
};

// Main Marketing Efficiency component
export const MarketingEfficiencyBlock = ({ data, loading }) => {
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [thresholdsLoaded, setThresholdsLoaded] = useState(false);
  
  // Fetch thresholds from Company Targets settings
  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        const response = await axios.get(`${API}/api/marketing/pacing/targets`);
        if (response.data?.efficiency_thresholds) {
          setThresholds(response.data.efficiency_thresholds);
        }
      } catch (error) {
        console.error('Error fetching efficiency thresholds:', error);
        // Fall back to defaults
      } finally {
        setThresholdsLoaded(true);
      }
    };
    fetchThresholds();
  }, []);
  
  // Build combined thresholds with metadata
  const EFFICIENCY_THRESHOLDS = Object.keys(THRESHOLD_META).reduce((acc, key) => {
    acc[key] = {
      ...THRESHOLD_META[key],
      target: thresholds[key]?.target || DEFAULT_THRESHOLDS[key]?.target,
      max: thresholds[key]?.max,
      min: thresholds[key]?.min
    };
    return acc;
  }, {});
  
  if (loading || !thresholdsLoaded) {
    return (
      <Card className="dark:bg-slate-800/60" data-testid="marketing-efficiency-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            Marketing Efficiency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-40 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const metrics = data?.metrics || {};
  
  // Calculate efficiency metrics from API data
  // CPL, CPS, CPSale use: Ad Spend (manual/cometly) / Builder Prime counts
  // ROAS, COM use: Builder Prime revenue / Ad Spend
  const spend = metrics.spend?.actual || 0;
  const leads = metrics.leads?.actual || 0;
  const appointments = metrics.appointments?.actual || 0;
  const sales = metrics.sales?.actual || 0;
  const revenue = metrics.revenue?.actual || 0;
  
  const efficiencyData = {
    // CPL = Ad Spend / Leads (BP leads exclude dead/canceled)
    cpl: leads > 0 ? spend / leads : null,
    // CPS = Ad Spend / Appointments Set (BP appointments exclude no-shows)
    cps: appointments > 0 ? spend / appointments : null,
    // CPSale = Ad Spend / Sales (BP actual closed deals)
    cpsale: sales > 0 ? spend / sales : null,
    // ROAS = Revenue / Ad Spend (BP revenue / manual spend)
    roas: spend > 0 ? revenue / spend : null,
    // COM% = Ad Spend / Revenue * 100
    com: revenue > 0 ? (spend / revenue) * 100 : null,
  };
  
  // Data source info for display
  const dataSourceInfo = {
    spend: metrics.spend?.source || 'manual',
    leads: metrics.leads?.source || 'builderprime',
    appointments: metrics.appointments?.source || 'builderprime',
    sales: metrics.sales?.source || 'builderprime',
    revenue: metrics.revenue?.source || 'builderprime'
  };
  
  // Count status summary
  const statusCounts = Object.keys(EFFICIENCY_THRESHOLDS).reduce((acc, key) => {
    const status = getEfficiencyStatus(efficiencyData[key], EFFICIENCY_THRESHOLDS[key]);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="dark:bg-slate-800/60" data-testid="marketing-efficiency-block">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            Marketing Efficiency
          </CardTitle>
          <div className="flex items-center gap-2">
            {statusCounts.excellent > 0 && (
              <Badge className="bg-green-500 text-xs">{statusCounts.excellent} Excellent</Badge>
            )}
            {statusCounts.acceptable > 0 && (
              <Badge className="bg-yellow-500 text-xs">{statusCounts.acceptable} At Risk</Badge>
            )}
            {statusCounts.poor > 0 && (
              <Badge className="bg-red-500 text-xs">{statusCounts.poor} Above Max</Badge>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          L10 efficiency thresholds vs actual performance (from Company Targets)
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(EFFICIENCY_THRESHOLDS).map(([key, threshold]) => (
            <EfficiencyGauge
              key={key}
              metricKey={key}
              actual={efficiencyData[key]}
              threshold={threshold}
            />
          ))}
        </div>
        
        {/* Summary */}
        <div className="mt-4 pt-4 border-t dark:border-slate-700 text-xs text-muted-foreground">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" /> Below Target (Excellent)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" /> Between Target & Max
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Above Max (Action Needed)
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground/70">
              Data: Spend (${spend > 0 ? (spend/1000).toFixed(0) + 'K' : '0'}) · Leads ({leads}) · Appts ({appointments}) · Sales ({sales}) · Rev (${revenue > 0 ? (revenue/1000).toFixed(0) + 'K' : '0'})
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketingEfficiencyBlock;
